import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Card, Dropdown } from "react-bootstrap";
import { FaCalendarAlt } from "react-icons/fa";
import "./PreferenceJob.css";
import { useNavigate, useLocation } from 'react-router-dom';
import JobSeekerNavbar from "../../../../Pages/Navbar/JobSeekerNavbar";
import Swal from 'sweetalert2';
import BaseUrl from '../../../../ApiServices/BaseUrl';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

function PreferenceJob() {
  const location = useLocation();
  const { userId, userName, current_country, job_position } = location.state || {};

  // Log userId to console
  useEffect(() => {
    console.log('Received userId:', userId);
    console.log('Received userName:', userName);
    console.log('Received current_country:', current_country);
    console.log('Received job_position:', job_position);
  }, [userId, current_country, job_position]);

  const [formData, setFormData] = useState({
    jobLocation: "",
    minSalary: "",
    maxSalary: "",
    currency: "",
    startDate: "",
    startOption: "",
    jobPosition: "",
    dayOffPreference: "",
    accommodationPreference: "",
    negotiable: false
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const navigate = useNavigate();

  // Helper function to filter options for dropdown
  const filterOptions = (options, searchTerm) => {
    if (!searchTerm) return options;
    return options.filter(option =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Custom Dropdown Component
  const CustomDropdown = ({
    title,
    field,
    options,
    selectedValue,
    onSelect,
    required = false,
    menuClassName = '',
    disabled = false
  }) => {
    const [localSearchTerm, setLocalSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const filteredOptions = filterOptions(options, localSearchTerm);

    return (
      <div>
        <Dropdown
          className="mb-3"
          show={isOpen}
          onToggle={(isOpen) => setIsOpen(isOpen)}
          disabled={disabled}
        >
          <Dropdown.Toggle
            variant="light"
            className="preference-job-select w-100 d-flex justify-content-between align-items-center"
            id={`dropdown-${field}`}
            disabled={disabled}
          >
            {selectedValue || `${title}${required ? ' *' : ''}`}
          </Dropdown.Toggle>
          <Dropdown.Menu className={`w-100 ${menuClassName}`}>
            <div className="px-2 py-1">
              <Form.Control
                type="text"
                placeholder={`Search ${title.toLowerCase()}...`}
                value={localSearchTerm}
                onChange={(e) => setLocalSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="mb-2"
                autoFocus
              />
            </div>
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <Dropdown.Item
                    key={option}
                    active={selectedValue === option}
                    onClick={() => {
                      onSelect(field, option);
                      setLocalSearchTerm('');
                      setIsOpen(false);
                    }}
                  >
                    {option}
                  </Dropdown.Item>
                ))
              ) : (
                <Dropdown.Item disabled>No matches found</Dropdown.Item>
              )}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  };

  // Fetch existing data
  useEffect(() => {
    const fetchJobPreferenceData = async () => {
      if (!userId) return;

      try {
        setIsLoading(true);
        const response = await fetch(`${BaseUrl}/api/job-seeker/doctor/${userId}`);

        if (response.ok) {
          const data = await response.json();
          console.log('Fetched job preference data:', data);

          // Set job preference fields
          if (data.dtr_preferred_job_location && data.dtr_preferred_job_location !== "null") {
            try {
              const parsedLocations = JSON.parse(data.dtr_preferred_job_location);
              if (Array.isArray(parsedLocations) && parsedLocations.length > 0) {
                setFormData(prev => ({ ...prev, jobLocation: parsedLocations[0] }));
              }
            } catch (e) {
              console.error('Error parsing job locations:', e);
            }
          }

          if (data.dtr_min_monthly_sal !== null && data.dtr_min_monthly_sal !== undefined) {
            setFormData(prev => ({ ...prev, minSalary: data.dtr_min_monthly_sal.toString() }));
          }

          if (data.dtr_max_monthly_sal !== null && data.dtr_max_monthly_sal !== undefined) {
            setFormData(prev => ({ ...prev, maxSalary: data.dtr_max_monthly_sal.toString() }));
          }

          if (data.dtr_country_currency && data.dtr_country_currency !== "null") {
            setFormData(prev => ({ ...prev, currency: data.dtr_country_currency }));
          }

          if (data.dtr_negotiable !== null && data.dtr_negotiable !== undefined) {
            setFormData(prev => ({ ...prev, negotiable: Boolean(data.dtr_negotiable) }));
          }

          if (data.dtr_job_start_date && data.dtr_job_start_date !== "null") {
            // Check if it's "immediately" (usually a recent date or empty)
            if (data.dtr_job_start_date === "immediately" ||
              data.dtr_job_start_date === "Immediately" ||
              data.dtr_job_start_date.toLowerCase().includes("immediate")) {
              setFormData(prev => ({
                ...prev,
                startOption: 'immediately',
                startDate: ''
              }));
            } else {
              // Try to parse as date
              const startDate = new Date(data.dtr_job_start_date);
              if (!isNaN(startDate.getTime())) {
                // Format date for input field (YYYY-MM-DD)
                setFormData(prev => ({
                  ...prev,
                  startDate: startDate.toISOString().split('T')[0],
                  startOption: ''
                }));
              }
            }
          }
        } else if (response.status !== 404) {
          throw new Error('Failed to fetch job preference data');
        }
      } catch (err) {
        console.error('Error fetching job preference data:', err);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to load your job preference information. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#d33',
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (userId) {
      fetchJobPreferenceData();
    }
  }, [userId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSelect = (field, value) => {
    setFormData({ ...formData, [field]: value });

    // Special handling for startOption
    if (field === 'startOption' && value === 'immediately') {
      setFormData(prev => ({
        ...prev,
        startOption: 'immediately',
        startDate: ''
      }));
    } else if (field === 'startDate') {
      // If a date is selected, clear the immediately option
      setFormData(prev => ({
        ...prev,
        startOption: '',
        startDate: value
      }));
    }
  };

  const handleSave = async () => {
    if (!userId) {
      Swal.fire({
        title: 'Error!',
        text: 'User ID is missing. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
      return false;
    }

    setIsSaving(true);

    try {
      // Prepare the data object for API
      const apiData = {
        user_id: userId,
        dtr_user_id: userId,
        dtr_preferred_job_location: formData.jobLocation ? [formData.jobLocation] : [],
        dtr_min_monthly_sal: formData.minSalary ? parseFloat(formData.minSalary) : null,
        dtr_max_monthly_sal: formData.maxSalary ? parseFloat(formData.maxSalary) : null,
        dtr_country_currency: formData.currency || "",
        dtr_negotiable: formData.negotiable,
        dtr_job_start_date: formData.startOption === 'immediately' ? "immediately" : formData.startDate,
        dtr_job_position_for_applying: formData.jobPosition || "",
        dtr_day_off_preference: formData.dayOffPreference || "",
        dtr_accommodation_preference: formData.accommodationPreference || ""
      };

      console.log('Saving job preference data:', apiData);

      const response = await fetch(`${BaseUrl}/api/job-seeker/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData),
      });

      if (response.ok) {
        const result = await response.json();
        Swal.fire({
          title: 'Success!',
          text: 'Job preferences saved successfully!',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#3085d6',
        });
        console.log('API Response:', result);
        return true;
      } else {
        throw new Error('Failed to save job preferences');
      }
    } catch (error) {
      console.error('Error saving job preferences:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Error saving job preferences. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    // Save data first, then navigate
    const success = await handleSave();
    if (success) {
      navigate('/experince', {
        state: { userId, userName, current_country, job_position }
      });
    }
  };

  const handleBack = () => {
    navigate('/professionalinformation', {
      state: { userId, userName, current_country, job_position }
    });
  };

  // Open date picker
  const openDatePicker = () => {
    setShowDatePicker(true);
    // Focus on date input
    setTimeout(() => {
      const dateInput = document.querySelector('.preference-job-date-input');
      if (dateInput) dateInput.showPicker ? dateInput.showPicker() : dateInput.focus();
    }, 100);
  };

  // Options arrays
  const jobLocations = [
    "UAE",
    "Dubai",
    "Saudi Arabia",
    "Qatar",
    "Kuwait",
    "Bahrain",
    "Oman",
    "Brunei",
    "Singapore",
    "Malaysia",
    "Hong Kong",
    "Japan",
    "South Korea",
    "Taiwan",
    "Germany",
    "Netherlands",
    "United Kingdom",
    "Italy",
    "Spain",
    "France",
    "Poland",
    "Portugal",
    "Romania",
    "Hungary",
    "Canada",
    "United States",
    "Australia",
    "New Zealand",
    "India" // Keeping India from your original array
  ];
  const currencies = ["INR", "USD", "QAR", "AED", "SAR", "EUR", "GBP"];

  const jobPositions = [
    "Resident Doctor",
    "Anaesthetist",
    "Specialist Doctor",
    "Cardiologist",
    "Consultant Doctor",
    "Dermatologist",
    "General Practitioner (GP)",
    "Endocrinologist",
    "Emergency Medicine Doctor",
    "Gastroenterologist",
    "Surgeon",
    "Nephrologist",
    "Medical Officer",
    "Neurologist",
    "Research Doctor",
    "Oncologist",
    "Academic Doctor",
    "Orthopaedic Surgeon",
    "Locum Doctor",
    "Paediatrician",
    "Psychiatrist",
    "Radiologist",
    "Urologist"
  ];

  const dayOffOptions = ["Friday", "Saturday", "Sunday", "Flexible", "I don't want", "To be discussed"];
  const accommodationOptions = ["Live in/Live Out", "Live in", "Live out", "Sharing room", "Separate room", "Flexible", "To be discussed"];

  if (isLoading) {
    return (
      <>
        <JobSeekerNavbar />
        <div className="preference-job-loading d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <JobSeekerNavbar />
      <div className="preference-job-container" style={{ marginTop: "84px", width: '96%', marginLeft: '2%' }}>
        <Container className="mt-4 px-4 pb-5">
          <Card className="shadow border-0 professional-info-card">
            <Card.Header className="professional-info-header">
              <h2 className="m-0 text-white text-center">
                <i className="bi bi-briefcase me-2"></i>
                Job Preferences
              </h2>
            </Card.Header>
            <Card.Body className="professional-info-body">
              <Form className="preference-job-form">

                {/* Job Location */}
                <div className="mb-5">
                  <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                    What is your preferred job location? <span className="text-danger">*</span>
                  </h4>

                  <CustomDropdown
                    title="Select Preferred Job Location"
                    field="jobLocation"
                    options={jobLocations}
                    selectedValue={formData.jobLocation}
                    onSelect={handleSelect}
                    required={true}
                    menuClassName="personalinfo-nationality-menu"
                  />

                  <Form.Text className="preference-job-form-text mt-2">
                    You can select any or maximum 4 country
                  </Form.Text>
                </div>

                {/* Expected Salary */}
                <div className="mb-5">
                  <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                    Expected monthly salary <span className="text-danger">*</span>
                  </h4>

                  <Row className="g-3 mb-3">
                    <Col xs={12} md={4}>
                      <Form.Control
                        type="number"
                        placeholder="Type Minimum salary"
                        className="preference-job-input form-control"
                        name="minSalary"
                        value={formData.minSalary}
                        onChange={handleChange}
                      />
                    </Col>
                    <Col xs={12} md={4}>
                      <Form.Control
                        type="number"
                        placeholder="Type Maximum salary"
                        className="preference-job-input form-control"
                        name="maxSalary"
                        value={formData.maxSalary}
                        onChange={handleChange}
                      />
                    </Col>
                    <Col xs={12} md={4}>
                      <CustomDropdown
                        title="Select Currency"
                        field="currency"
                        options={currencies}
                        selectedValue={formData.currency}
                        onSelect={handleSelect}
                        required={true}
                        menuClassName="personalinfo-nationality-menu"
                      />
                    </Col>
                  </Row>

                  <div className="mt-3">
                    <Button
                      variant={formData.negotiable ? "primary" : "outline-primary"}
                      className={`preference-job-negotiable-btn ${formData.negotiable ? 'preference-job-selected-btn' : ''}`}
                      onClick={() => handleSelect('negotiable', !formData.negotiable)}
                      style={{ minWidth: '150px' }}
                    >
                      Negotiable
                    </Button>
                  </div>
                </div>

                {/* Start Date */}
                {/* Start Date */}
                {/* Start Date */}
                <div className="mb-5">
                  <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                    When would you like to start your job?
                  </h4>

                  <div className="row g-2 align-items-center mb-3">
                    {/* Immediate Button - col-6 */}
                    <div className="col-6">
                      <Button
                        className={`startdate-btn w-100 ${formData.startOption === 'immediate' ? 'selected-button' : ''}`}
                        onClick={() => handleSelect('startOption', 'immediate')}
                      >
                        Immediate
                      </Button>
                    </div>

                    {/* DatePicker - col-6 */}
                    <div className="col-6">
                      <DatePicker
                        selected={
                          formData.startDate && formData.startDate !== 'Immediately'
                            ? (formData.startDate instanceof Date
                              ? formData.startDate
                              : new Date(formData.startDate))
                            : null
                        }
                        onChange={(date) => {
                          handleSelect('startDate', date);
                        }}
                        className="date-picker-input-startdate-preference w-125 form-control"
                        placeholderText="Select date"
                        minDate={new Date()}
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        yearDropdownItemNumber={15}
                        scrollableYearDropdown
                        dateFormat="MM/DD/YYYY"
                        autoFocus={false}
                        isClearable={true}
                        onKeyDown={(e) => {
                          // Prevent typing in the date picker
                          e.preventDefault();
                        }}
                      />
                    </div>
                  </div>

                  <Form.Text className="preference-job-form-text mt-2">
                    {/* Next Available date is required */}
                  </Form.Text>
                </div>

                {/* Job Position */}
                <div className="mb-5">
                  <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                    Which Job position you are applying for? <span className="text-danger">*</span>
                  </h4>

                  <CustomDropdown
                    title="Select Job Position"
                    field="jobPosition"
                    options={jobPositions}
                    selectedValue={formData.jobPosition}
                    onSelect={handleSelect}
                    required={true}
                    menuClassName="personalinfo-nationality-menu"
                  />
                </div>

                {/* Day off Preference */}
                <div className="mb-5">
                  <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                    Day off Preference
                  </h4>

                  <Row className="g-3">
                    {dayOffOptions.map(option => (
                      <Col xs={6} lg={3} key={option}>
                        <Button
                          type="button"
                          className={`language-btn jobtype-btn w-100 ${formData.dayOffPreference === option ? 'selected-button' : ''}`}
                          onClick={() => handleSelect('dayOffPreference', option)}
                        >
                          {option}
                        </Button>
                      </Col>
                    ))}
                  </Row>
                </div>

                {/* Accommodation Preference */}
                <div className="mb-5">
                  <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                    Accommodation Preference <span className="text-danger">*</span>
                  </h4>

                  <Row className="g-3 mb-3">
                    {accommodationOptions.map(option => (
                      <Col xs={6} lg={3} key={option}>
                        <Button
                          type="button"
                          className={`language-btn jobtype-btn w-100 ${formData.accommodationPreference === option ? 'selected-button' : ''}`}
                          onClick={() => handleSelect('accommodationPreference', option)}
                        >
                          {option}
                        </Button>
                      </Col>
                    ))}
                  </Row>

                  <Form.Text className="preference-job-form-text mt-2">
                    Accommodation preference is required
                  </Form.Text>
                </div>

                {/* Navigation Buttons */}
                <div className="additional-section mb-4" style={{ marginTop: '20px' }}>
                  <div className="d-flex justify-content-between gap-2" style={{ width: "100%" }}>
                    <button
                      className="nav-btn"
                      style={{ flex: 1 }}
                      onClick={handleBack}
                      disabled={isSaving}
                    >
                      Back
                    </button>

                    <button
                      className="nav-btn"
                      style={{ flex: 1 }}
                      onClick={handleSave}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>

                    <button
                      className="nav-btn"
                      style={{ flex: 1 }}
                      onClick={handleNext}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Saving...' : 'Next'}
                    </button>
                  </div>
                </div>

              </Form>
            </Card.Body>
          </Card>
        </Container>
      </div>
    </>
  );
}

export default PreferenceJob;